/*
 * Copyright (c) 2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import { Suspense, lazy, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@vaas/react-ui-kit/auth';

import { BootScreen } from '@/components/loading';

// Split out because the signed-in path — every visit after the first — never renders it.
const SignInPage = lazy(() => import('./SignInPage'));

/**
 * Guards everything behind a session.
 *
 * <p>This used to be the kit's own guard, which sends an unauthenticated visitor straight to
 * Entra. That skipped the one screen that says which application is asking, so a cold start was a
 * blank page followed by an unexplained Microsoft form. The guard now stops at
 * {@link SignInPage} and lets the visitor start the redirect themselves.
 *
 * <p>Mock mode signs in on load, so local development never reaches the sign-in screen unless it
 * is asked for.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { t } = useTranslation();
  const { status } = useAuth();

  // MSAL is still processing a redirect response or restoring a cached account. Rendering the
  // sign-in screen here would flash it at someone who is already signed in.
  if (status === 'loading') {
    return <BootScreen status={t('signIn.checking')} />;
  }

  if (status !== 'authenticated') {
    return (
      <Suspense fallback={<BootScreen />}>
        <SignInPage />
      </Suspense>
    );
  }

  return <>{children}</>;
}
